import { useState } from "react";
import { motion } from "framer-motion";
import { FaEnvelope } from "react-icons/fa";
import emailjs from "@emailjs/browser";
import { useLang } from "../../context/LangContext";
import "./ContactSection.css";

// ✅ Credenciais do EmailJS configuradas para o projeto
const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || "service_fsk4yj3";
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "template_sna0r7k";
const USER_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "W7x_RJt4nQ5dcZQNe";

// Validação de email
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ContactSection() {
  const { t } = useLang();
  const c = t.contact;

  // ✅ State melhorado com validação
  const [formData, setFormData] = useState({
    from_name: "",
    reply_to: "",
    subject: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  /**
   * ✅ Validação robusta do formulário
   */
  function validateForm(data) {
    const newErrors = {};

    // Validar nome
    if (!data.from_name?.trim()) {
      newErrors.from_name = "Nome é obrigatório";
    } else if (data.from_name.trim().length < 3) {
      newErrors.from_name = "Nome deve ter no mínimo 3 caracteres";
    }

    // Validar email
    if (!data.reply_to?.trim()) {
      newErrors.reply_to = "E-mail é obrigatório";
    } else if (!EMAIL_REGEX.test(data.reply_to)) {
      newErrors.reply_to = "E-mail inválido";
    }

    // Validar mensagem
    if (!data.message?.trim()) {
      newErrors.message = "Mensagem é obrigatória";
    } else if (data.message.trim().length < 10) {
      newErrors.message = "Mensagem deve ter no mínimo 10 caracteres";
    }

    return newErrors;
  }

  /**
   * ✅ Atualizar campo do formulário
   */
  function handleChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Limpar erro do campo ao digitar
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  }

  /**
   * ✅ Enviar formulário com validação e tratamento de erros
   */
  function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);

    // Validar dados
    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setLoading(false);
      return;
    }

    // Gerar timestamp
    const now = new Date();
    const timeString =
      now.toLocaleDateString("pt-BR") +
      " " +
      now.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      });

    // Preparar dados para envio
    const dataToSend = {
      ...formData,
      time: timeString,
    };

    // Verificar credenciais
    if (!SERVICE_ID || !TEMPLATE_ID || !USER_KEY) {
      console.error("EmailJS credentials não configuradas");
      setSent("error");
      setLoading(false);
      return;
    }

    // ✅ Enviar email com tratamento de erro robusto
    emailjs
      .send(SERVICE_ID, TEMPLATE_ID, dataToSend, USER_KEY)
      .then(() => {
        setSent(true);
        setFormData({
          from_name: "",
          reply_to: "",
          subject: "",
          message: "",
        });
        setErrors({});
        setLoading(false);

        // Limpar mensagem de sucesso após 5 segundos
        setTimeout(() => setSent(false), 5000);
      })
      .catch((error) => {
        console.error("Erro ao enviar email:", error);
        setSent("error");
        setLoading(false);

        // Limpar mensagem de erro após 5 segundos
        setTimeout(() => setSent(false), 5000);
      });
  }

  return (
    <section className="contact-section" id="contact">
      <div className="contact-container">
        {/* Lado esquerdo - Info */}
        <motion.div
          className="contact-side contact-side-left"
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
        >
          <span className="contact-code">{c.tag}</span>
          <h1 className="contact-title">
            {c.title}
            <br />
            {c.subtitle}
            <span className="contact-highlight">.</span>
          </h1>
          <p className="contact-desc">{c.desc}</p>
          <div className="contact-email">
            <FaEnvelope className="contact-icon" />
            <a href="mailto:alansouzaesi@gmail.com">alansouzaesi@gmail.com</a>
          </div>
        </motion.div>

        {/* Lado direito - Formulário */}
        <motion.form
          className="contact-side contact-form"
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          onSubmit={handleSubmit}
          noValidate // ✅ Desabilitar validação HTML para usar custom
        >
          <div className="contact-form-title">{c.formTitle}</div>

          {/* Campo: Nome */}
          <div className="form-group">
            <input
              name="from_name"
              type="text"
              className={`form-input ${errors.from_name ? "form-input--error" : ""}`}
              placeholder={c.name}
              value={formData.from_name}
              onChange={handleChange}
              aria-label="Seu nome completo"
              aria-invalid={!!errors.from_name}
              aria-describedby={errors.from_name ? "error-from_name" : undefined}
            />
            {errors.from_name && (
              <span
                id="error-from_name"
                className="form-error-message"
                role="alert"
              >
                {errors.from_name}
              </span>
            )}
          </div>

          {/* Campo: Email */}
          <div className="form-group">
            <input
              name="reply_to"
              type="email"
              className={`form-input ${errors.reply_to ? "form-input--error" : ""}`}
              placeholder={c.email}
              value={formData.reply_to}
              onChange={handleChange}
              aria-label="Seu endereço de e-mail"
              aria-invalid={!!errors.reply_to}
              aria-describedby={errors.reply_to ? "error-reply_to" : undefined}
            />
            {errors.reply_to && (
              <span
                id="error-reply_to"
                className="form-error-message"
                role="alert"
              >
                {errors.reply_to}
              </span>
            )}
          </div>

          {/* Campo: Assunto (opcional) */}
          <div className="form-group">
            <input
              name="subject"
              type="text"
              className="form-input"
              placeholder={c.subject}
              value={formData.subject}
              onChange={handleChange}
              aria-label="Assunto da mensagem (opcional)"
            />
          </div>

          {/* Campo: Mensagem */}
          <div className="form-group">
            <textarea
              name="message"
              className={`form-input ${errors.message ? "form-input--error" : ""}`}
              placeholder={c.message}
              rows={4}
              value={formData.message}
              onChange={handleChange}
              aria-label="Sua mensagem"
              aria-invalid={!!errors.message}
              aria-describedby={errors.message ? "error-message" : undefined}
            />
            {errors.message && (
              <span
                id="error-message"
                className="form-error-message"
                role="alert"
              >
                {errors.message}
              </span>
            )}
          </div>

          {/* Botão de envio */}
          <button
            type="submit"
            className="form-btn"
            disabled={loading || Object.keys(errors).length > 0}
            aria-label={loading ? c.sending : c.send}
          >
            {loading ? c.sending : c.send}
          </button>

          {/* Mensagem de sucesso */}
          {sent === true && (
            <span className="form-success" role="status" aria-live="polite">
              ✅ {c.success}
            </span>
          )}

          {/* Mensagem de erro */}
          {sent === "error" && (
            <span className="form-error" role="alert" aria-live="assertive">
              ❌ {c.error}
            </span>
          )}
        </motion.form>
      </div>
    </section>
  );
}